import axios, { AxiosError } from "axios"
import {
  requestInterceptorErrorSecurityCredentials,
  requestInterceptorSuccessCredentials,
  responseInterceptorErrorCredentials,
  responseInterceptorSuccessCredentials,
} from "interceptors"
import { IDocumentProcessedFilters } from "types"
import {
  ADDITIONAL_INFO_PATH,
  ALL_PATH,
  AND_PARAM,
  CHANGE_PATH,
  COUNT_PATH,
  DOCUMENT_PATH,
  DOCUMENT_TYPES_PARAM,
  FLOW_PATH,
  MANAGE_PATH,
  PATH_RETENTION,
  RECEIVER_RUC_PARAM,
  REIMBURSEMENT_TAX_PLAN_PATH,
  REJECTED_PATH,
  SEARCH_PARAM,
  STATUS_PATH,
  SUPPORT_PATH,
} from "../../constants/paths"

const instance = axios.create({
  baseURL: process.env.REACT_APP_DOCUMENTS_API_SERVICE_DOMAIN,
})
instance.interceptors.request.use(
  requestInterceptorSuccessCredentials,
  requestInterceptorErrorSecurityCredentials
)

instance.interceptors.response.use(
  responseInterceptorSuccessCredentials,
  responseInterceptorErrorCredentials
)

export const getDocumentsService = async (
  filters: IDocumentProcessedFilters,
  pageSize: number,
  offset: number,
  dashboard?: string
) => {
  try {
    const result = await instance.get(DOCUMENT_PATH, {
      params: {
        documentTypes: filters.documentTypes,
        conditions: filters.conditions,
        historyTypes: filters.historyTypes,
        rucOrName: filters.rucOrName,
        rucOrNameReceptor: filters.rucOrNameReceptor,
        documentRangeEnd: filters.documentRangeEnd,
        documentRangeStart: filters.documentRangeStart,
        serialNumber: filters.serialNumber,
        receptionRangeEnd: filters.receptionRangeEnd,
        receptionRangeStart: filters.receptionRangeStart,
        pageSize,
        offset,
        dashboard,
      },
    })

    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const createDocumentService = async (document: any, userId: any) => {
  try {
    const result = await instance.post(DOCUMENT_PATH, { ...document, userId })
    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const updateDocumentAdditionalInfoService = async (
  documentId: any,
  additionalInfo: string
) => {
  try {
    const result = await instance.put(
      `${DOCUMENT_PATH}/${documentId}${ADDITIONAL_INFO_PATH}`,
      { additionalInfo }
    )
    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const getDocumentByIdService = async (id: any) => {
  try {
    const result = await instance.get(DOCUMENT_PATH + "/" + id)
    return result.data
  } catch (error: any) {
    console.log(error)
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const queueDocumentByIdService = async (id: any) => {
  try {
    const result = await instance.get(
      DOCUMENT_PATH + STATUS_PATH + FLOW_PATH + "/" + id
    )
    return result.data
  } catch (error: any) {
    console.log(error)
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const manageDocumentByIdService = async (
  id: any,
  emails: string,
  subject: string,
  message: string
) => {
  try {
    const result = await instance.post(DOCUMENT_PATH + MANAGE_PATH + "/" + id, {
      emails,
      subject,
      message,
    })
    return result.data
  } catch (error: any) {
    console.log(error)
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const getSupportDocumentByAuthorizationNumberService = async (
  authorizationNumber: any
) => {
  try {
    const result = await instance.get(
      DOCUMENT_PATH + SUPPORT_PATH + "/" + authorizationNumber
    )
    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data?.message)
  }
}

export const updateDocumentStatusService = async (
  documentId: any,
  statusId: any,
  userId: any
) => {
  try {
    const result = await instance.post(DOCUMENT_PATH + STATUS_PATH, {
      documentId,
      statusId,
      userId,
    })
    return result.data
  } catch (error: any) {
    console.log(JSON.stringify(error))
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const approveDocumentService = async (
  documentStatus: any,
  documentData: any
) => {
  try {
    const result = await instance.post(
      DOCUMENT_PATH + STATUS_PATH + CHANGE_PATH,
      { documentStatus, documentData }
    )
    return result.data
  } catch (error: any) {
    console.log(error)
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const rejectDocumentService = async (
  documentStatus: any,
  documentData: any
) => {
  try {
    const result = await instance.post(
      DOCUMENT_PATH + STATUS_PATH + CHANGE_PATH,
      { documentStatus, documentData }
    )
    return result.data
  } catch (error: any) {
    console.log(error)
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const rejectRetentionService = async (retentionId: number) => {
  try {
    const result = await instance.post(
      `${DOCUMENT_PATH}${REJECTED_PATH}${PATH_RETENTION}/${retentionId}`
    )
    return result.data
  } catch (error: any) {
    console.log(error)
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const getReimbursementTaxPlan = async () => {
  try {
    const result = await instance.get(
      DOCUMENT_PATH + REIMBURSEMENT_TAX_PLAN_PATH
    )
    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const countDashboardDocumentsService = async () => {
  try {
    const result = await instance.get(DOCUMENT_PATH + COUNT_PATH)
    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const getAllDocumentsByParamsService = async (
  types: number[],
  receiverRuc: string
) => {
  try {
    const result = await instance.get(
      DOCUMENT_PATH +
        ALL_PATH +
        SEARCH_PARAM +
        DOCUMENT_TYPES_PARAM +
        types.join(",") +
        AND_PARAM +
        RECEIVER_RUC_PARAM +
        receiverRuc
    )
    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const wsGetEmailsFromSupplier = async (documetnId: any) => {
  try {
    const result = await instance.get("/vensecar/retention/mails/" + documetnId)
    return result.data
  } catch (error: any) {
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}

export const wsResendEmailsFromRetention = async (documetnId: any) => {
  try {
    const result = await instance.get(
      "/vensecar/retention/resend/" + documetnId
    )
    return result.data
  } catch (error: any) {
    console.log(error)
    const e: AxiosError = error
    throw new Error(e.response?.data)
  }
}
